import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, UserMinus, Mail, MessageCircle, Loader2, ScanSearch, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeparturesLog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [departures, setDepartures] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [processing, setProcessing] = useState(null);

  const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

  const loadDepartures = () => {
    axios
      .get(`${BACKEND_URL}/api/discord/departures`, authHdr())
      .then((res) => setDepartures(res.data.departures || []))
      .catch(() => toast.error("Failed to load departures"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDepartures();
  }, []);

  const runScan = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/discord/departures/scan`, authHdr());
      setScanResult(res.data);
      if ((res.data.total || 0) === 0) toast.success("No missed departures — everyone linked is still in the server");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed — Discord bot may be offline");
    } finally {
      setScanning(false);
    }
  };

  const processDeparture = async (candidate) => {
    setProcessing(candidate.discord_id);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/discord/departures/process/${candidate.discord_id}`, {}, authHdr()
      );
      toast.success(`Departure processed: ${res.data.who}`);
      setScanResult((prev) => prev && {
        ...prev,
        missing_from_discord: prev.missing_from_discord.filter((c) => c.discord_id !== candidate.discord_id),
        total: prev.total - 1,
      });
      loadDepartures();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to process departure");
    } finally {
      setProcessing(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
      });
    } catch (e) {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/")}
            className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700" data-testid="departures-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <UserMinus className="w-6 h-6 text-red-400" /> Discord Departures
            </h1>
            <p className="text-slate-400 text-sm">Who left the server, when, and which officers were notified</p>
          </div>
          <Button size="sm" onClick={runScan} disabled={scanning}
            className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="departures-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ScanSearch className="w-4 h-4 mr-1.5" />}
            {scanning ? "Scanning…" : "Scan for Missed"}
          </Button>
        </div>

        {scanResult && scanResult.total > 0 && (
          <Card className="bg-amber-950/30 border-amber-800" data-testid="departures-scan-results">
            <CardContent className="p-4 space-y-3">
              <p className="text-amber-300 text-sm font-semibold">
                {scanResult.total} linked profile{scanResult.total > 1 ? "s are" : " is"} no longer in the Discord server:
              </p>
              {scanResult.missing_from_discord.map((c) => (
                <div key={c.discord_id}
                  className="flex flex-wrap items-center justify-between gap-2 bg-slate-900/50 border border-slate-700 rounded-lg p-3"
                  data-testid={`missed-departure-${c.discord_id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{c.handle}</span>
                    <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">
                      {c.profile_type}{c.chapter ? ` · ${c.chapter}` : ""}
                    </Badge>
                    {c.already_logged && (
                      <Badge className="bg-slate-700/60 text-slate-300 border-slate-600">Already logged</Badge>
                    )}
                  </div>
                  <Button size="sm" onClick={() => processDeparture(c)}
                    disabled={processing === c.discord_id}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid={`process-departure-${c.discord_id}`}>
                    {processing === c.discord_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      : <Play className="w-3.5 h-3.5 mr-1" />}
                    Process Departure
                  </Button>
                </div>
              ))}
              <p className="text-slate-500 text-xs">
                Processing notifies leadership, auto-archives the profile, and logs the departure below.
              </p>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : departures.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center text-slate-400" data-testid="departures-empty">
              No departures recorded yet. Alerts appear here when someone leaves the Discord server.
            </CardContent>
          </Card>
        ) : (
          departures.map((d, i) => (
            <Card key={i} className="bg-slate-800/50 border-slate-700" data-testid={`departure-${i}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">
                      {d.handle || d.display_name || d.discord_username}
                    </span>
                    {d.profile_type ? (
                      <Badge className="bg-blue-900/50 text-blue-300 border-blue-700">
                        {d.profile_type}{d.chapter ? ` · ${d.chapter}` : ""}
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-900/50 text-amber-300 border-amber-700">Not linked</Badge>
                    )}
                    {d.auto_archived && (
                      <Badge className="bg-red-900/50 text-red-300 border-red-700" data-testid={`departure-archived-${i}`}>
                        Auto-archived
                      </Badge>
                    )}
                    {d.archive_status === "skipped_hub_kick" && (
                      <Badge className="bg-slate-700/60 text-slate-300 border-slate-600">
                        Archived via hub
                      </Badge>
                    )}
                  </div>
                  <span className="text-slate-400 text-sm">{fmt(d.left_at)}</span>
                </div>
                <p className="text-slate-500 text-xs">Discord: @{d.discord_username}</p>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(d.notifications || []).map((n, j) => (
                    <span key={j}
                      className="inline-flex items-center gap-1 text-xs bg-slate-900/60 border border-slate-700 rounded-full px-2 py-1 text-slate-300">
                      {n.title} {n.chapter} — {n.handle}
                      <MessageCircle className={`w-3 h-3 ${n.dm_sent ? "text-green-400" : "text-slate-600"}`} />
                      <Mail className={`w-3 h-3 ${n.email_sent ? "text-green-400" : "text-slate-600"}`} />
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
